/**
 * Permission Service
 * 
 * Centralized permission checking service for the entire application.
 * Single source of truth for all feature access, tier checks, and resource validation.
 * 
 * @module permissionService
 */

import { USER_ROLE_FIELDS, USER_RESOURCES_LIST } from './constant';
import { FEATURES, FEATURE_PERMISSIONS } from './featurePermissions';
import { TIERS } from './roleConstants';

/**
 * Permission Service Class
 * 
 * Handles all permission-related logic:
 * - Tier detection (Free, Premium, Elite)
 * - Feature access validation
 * - Resource checking
 * - Upgrade messaging
 * - Plan display utilities
 */
export class PermissionService {
  /**
   * @param {Object} auth - Auth object from useAuth hook
   * @param {Array<string>} auth.role - User roles array (source of truth from backend)
   * @param {Array<string>} auth.resources - User resources array (source of truth from backend)
   */
  constructor(auth) {
    this.auth = auth || {};
    this.roles = Array.isArray(auth?.role) ? auth.role : [];
    this.resources = Array.isArray(auth?.resources) ? auth.resources : [];
  }

  // ==================== TIER CHECKS ====================
  
  /**
   * Check if user is on Free tier
   * Free tier = basic role only (no premium or pro1 roles)
   * @returns {boolean}
   */
  isFreeUser() {
    const hasBasic = this.roles.includes(USER_ROLE_FIELDS.BASIC);
    const hasPremium = this.roles.includes(USER_ROLE_FIELDS.PREMIUM);
    const hasPro1 = this.roles.includes(USER_ROLE_FIELDS.PRO1);
    
    // Free user = has basic role AND no premium/pro1 roles
    return hasBasic && !hasPremium && !hasPro1;
  }

  /**
   * Check if user is on Premium tier or higher
   * Premium = premium role OR pro1 role OR pro2 role (Elite and PRO2 users also have premium access)
   * Hierarchy: BASIC < PREMIUM < PRO1 (Elite) < PRO2 < ADMIN
   * @returns {boolean}
   */
  isPremiumUser() {
    const hasPremium = this.roles.includes(USER_ROLE_FIELDS.PREMIUM);
    const hasPro1 = this.roles.includes(USER_ROLE_FIELDS.PRO1);
    const hasPro2 = this.roles.includes(USER_ROLE_FIELDS.PRO2);
    
    return hasPremium || hasPro1 || hasPro2;
  }

  /**
   * Check if user is on Elite tier or higher
   * Elite requires pro1 role OR pro2 role (PRO2 users inherit Elite features)
   * Hierarchy: BASIC < PREMIUM < PRO1 (Elite) < PRO2 < ADMIN
   * Note: tbt_data resource determines polling speed, not access
   * @returns {boolean}
   */
  isEliteUser() {
    const hasPro1 = this.roles.includes(USER_ROLE_FIELDS.PRO1);
    const hasPro2 = this.roles.includes(USER_ROLE_FIELDS.PRO2);
    
    return hasPro1 || hasPro2;
  }

  /**
   * Check if user is on PRO2 tier
   * PRO2 is above Elite (PRO1) in hierarchy: BASIC < PREMIUM < PRO1 (Elite) < PRO2 < ADMIN
   * PRO2 users inherit PRO1 features, but PRO2-only features are exclusive to PRO2 role
   * @returns {boolean}
   */
  isPro2User() {
    const hasPro2 = this.roles.includes(USER_ROLE_FIELDS.PRO2);
    
    return hasPro2;
  }

  /**
   * Check if user has access to get Browser Push notifications
   * User has to be on a paid plan and should also have the browser_push resource
   * @returns {boolean}
   */
  hasPushNotificationAccess() {
    return this.isPremiumUser() && this.resources.includes(USER_RESOURCES_LIST.BROWSER_PUSH);
  }
  
  /**
   * Check if user has real-time data feed (tbt_data resource)
   * This determines polling speed, not feature access
   * @returns {boolean}
   */
  hasRealtimeDataFeed() {
    return this.resources.includes(USER_RESOURCES_LIST.TBT_FEED);
  }

  /**
   * Get current user tier
   * Hierarchy: BASIC < PREMIUM < PRO1 (Elite) < PRO2 < ADMIN
   * @returns {string} TIERS.ELITE, TIERS.PREMIUM, or TIERS.FREE
   */
  getCurrentTier() {
    if (this.isPro2User()) return TIERS.ELITE;  // PRO2 is highest tier (before admin)
    if (this.isEliteUser()) return TIERS.ELITE;
    if (this.isPremiumUser()) return TIERS.PREMIUM;
    return TIERS.FREE;
  }

  // ==================== FEATURE ACCESS ====================
  
  /**
   * Check if user can access a specific feature
   * Validates tier, roles, and resources
   * 
   * @param {string} featureName - Feature ID from FEATURES
   * @returns {boolean}
   */
  canAccessFeature(featureName) {
    const permission = FEATURE_PERMISSIONS[featureName];
    if (!permission) {
      console.warn(`[PermissionService] Unknown feature: ${featureName}`);
      return false;
    }

    // Check tier level
    const currentTier = this.getCurrentTier();
    const tierLevels = { [TIERS.FREE]: 0, [TIERS.PREMIUM]: 1, [TIERS.ELITE]: 2 };
    if (tierLevels[currentTier] < tierLevels[permission.requiredTier]) {
      return false;
    }

    // Check if user has at least one of the required roles
    const hasRole = permission.requiredRoles.some(role => 
      this.roles.includes(role)
    );
    if (!hasRole) return false;

    // Check resources (if any required)
    if (permission.requiredResources.length > 0) {
      const hasAllResources = permission.requiredResources.every(resource =>
        this.resources.includes(resource)
      );
      if (!hasAllResources) return false;
    }

    return true;
  }

  // ==================== INSTRUMENT ACCESS ====================
  
  /**
   * Check if user can access Index instruments
   * @returns {boolean}
   */
  canAccessIndexInstruments() {
    return true; // All users can access index instruments
  }

  /**
   * Check if user can access Stock instruments
   * Requires Premium+ with stocks resource
   * @returns {boolean}
   */
  canAccessStockInstruments() {
    return this.isPremiumUser() && 
           this.resources.includes(USER_RESOURCES_LIST.MP_STOCKS);
  }

  /**
   * Check if user can access Orderflow for Index
   * @returns {boolean}
   */
  canAccessOrderflowIndex() {
    return this.isEliteUser() && 
           this.resources.includes(USER_RESOURCES_LIST.OF_INDEX);
  }

  /**
   * Check if user can access Orderflow for Stocks
   * @returns {boolean}
   */
  canAccessOrderflowStocks() {
    return this.isEliteUser() && 
           this.resources.includes(USER_RESOURCES_LIST.OF_STOCKS);
  }

  // ==================== DATA FEED ACCESS ====================
  
  /**
   * Get data feed type available to user
   * Based on resources: tbt_data = realtime, 1min_data = live, none = delayed
   * @returns {'realtime'|'live'|'delayed'}
   */
  getDataFeedType() {
    // Elite: has tbt_data resource = realtime
    if (this.resources.includes(USER_RESOURCES_LIST.TBT_FEED)) {
      return 'realtime';
    }
    
    // Premium: has 1min_data resource = live
    if (this.resources.includes(USER_RESOURCES_LIST.ONE_MIN_DATA)) {
      return 'live';
    }
    
    // Free: no data resources = delayed (70 second polling)
    return 'delayed';
  }

  /**
   * Check if user has real-time NSE feed access
   * @deprecated Use hasRealtimeDataFeed() instead
   * @returns {boolean}
   */
  hasRealtimeAccess() {
    return this.hasRealtimeDataFeed();
  }

  // ==================== RESOURCE CHECKS ====================
  
  /**
   * Check if user has a specific resource
   * @param {string} resourceName - Resource name from USER_RESOURCES_LIST
   * @returns {boolean}
   */
  hasResource(resourceName) {
    return this.resources.includes(resourceName);
  }

  /**
   * Check if user has ANY of the provided resources
   * @param {Array<string>} resourceList - Array of resource names
   * @returns {boolean}
   */
  hasAnyResource(resourceList) {
    return resourceList.some(resource => this.resources.includes(resource));
  }

  /**
   * Check if user has ALL of the provided resources
   * @param {Array<string>} resourceList - Array of resource names
   * @returns {boolean}
   */
  hasAllResources(resourceList) {
    return resourceList.every(resource => this.resources.includes(resource));
  }

  // ==================== DISPLAY HELPERS ====================
  
  /**
   * Get normalized plan name for display
   * Determines plan from user's tier (roles + resources) - source of truth from backend
   * @returns {string}
   */
  getDisplayPlanName() {
    // Map tier to display name (capitalized, with FREE → 'Basic' mapping)
    const tierToDisplayName = {
      [TIERS.ELITE]: 'Elite',
      [TIERS.PREMIUM]: 'Professional',
      [TIERS.FREE]: 'Basic'
    };
    
    const currentTier = this.getCurrentTier();
    return tierToDisplayName[currentTier] || 'Basic';
  }

  getDisplayAccessLevel() {
    // Map tier to display name (capitalized, with FREE → 'Basic' mapping)
    const tierToDisplayName = {
      [TIERS.ELITE]: 'Elite',
      [TIERS.PREMIUM]: 'Professional',
      [TIERS.FREE]: 'Basic'
    };
    console.log("debug",this.canAccessIndexInstruments(),this.canAccessStockInstruments())
    const currentTier = this.getCurrentTier();
    let displayName = tierToDisplayName[currentTier] || 'Basic';
    if(displayName != "Basic"){
      if(!this.canAccessStockInstruments())
        displayName += " Index";
      if(this.hasResource(USER_RESOURCES_LIST.BSE_MP_INDEX))
        displayName += " + BSE"
    }
    return displayName;
  }

  /**
   * Get color code for current plan
   * @returns {string} Hex color code
   */
  getPlanColor() {
    const displayName = this.getDisplayPlanName();
    
    // Color scheme matches menu section colors for consistency
    const colors = {
      'Basic': '#FFFFFF',      // White - matches FREE ACCESS section
      'Professional': '#23B1BA',    // Cyan/Teal - matches PREMIUM ACCESS section
      'Elite': '#EC6C10'       // Orange - matches ELITE ACCESS section
    };
    
    return colors[displayName] || '#FFFFFF';
  }

  /**
   * Get badge color for drawer/header
   * @returns {string} Hex color code
   */
  getPlanBadgeColor() {
    return this.getPlanColor();
  }

  // ==================== UPGRADE MESSAGING ====================
  
  /**
   * Get upgrade message for a specific feature
   * @param {string} feature - Feature ID from FEATURES
   * @returns {string} User-friendly upgrade message
   */
  getUpgradeMessage(feature) {
    const permission = FEATURE_PERMISSIONS[feature];
    if (!permission) return 'Upgrade to access this feature';

    switch (permission.requiredTier) {
      case TIERS.PREMIUM:
        return 'Upgrade to Premium for advanced features and live data';
      case TIERS.ELITE:
        return 'Upgrade to Elite for real-time orderflow charts and advanced tools';
      default:
        return 'Upgrade to access this feature';
    }
  }

  /**
   * Get required tier for a feature
   * @param {string} feature - Feature ID from FEATURES
   * @returns {string} Tier name
   */
  getUpgradeTier(feature) {
    const permission = FEATURE_PERMISSIONS[feature];
    return permission?.requiredTier || TIERS.PREMIUM;
  }

  /**
   * Check if upgrade button should be shown
   * @returns {boolean}
   */
  shouldShowUpgradeButton() {
    return !this.isEliteUser(); // Show upgrade unless already Elite
  }

  /**
   * Get missing resources for a feature
   * @param {string} feature - Feature ID from FEATURES
   * @returns {Array<string>} List of missing resource names
   */
  getMissingResources(feature) {
    const permission = FEATURE_PERMISSIONS[feature];
    if (!permission) return [];
    
    return permission.requiredResources.filter(
      resource => !this.resources.includes(resource)
    );
  }

  // ==================== ROUTE ACCESS ====================
  
  /**
   * Check if user can access a route based on its config
   * @param {Object} routeConfig - Route configuration object
   * @param {Array<string>} routeConfig.roles - Required roles
   * @param {Array<string>} routeConfig.resources - Required resources
   * @returns {boolean}
   */
  canAccessRoute(routeConfig) {
    const { roles, resources } = routeConfig;
    
    // No role restriction = public route
    if (!roles || roles.length === 0) return true;
    
    // Check if user has at least one required role
    const hasRole = this.roles.some(role => roles.includes(role));
    if (!hasRole) return false;
    
    // Check resources if specified
    if (resources && resources.length > 0) {
      return this.hasAllResources(resources);
    }
    
    return true;
  }

  // ==================== ADMIN CHECKS ====================
  
  /**
   * Check if user is admin
   * @returns {boolean}
   */
  isAdmin() {
    return this.roles.includes(USER_ROLE_FIELDS.ADMIN);
  }

  // ==================== FEATURE-SPECIFIC PERMISSIONS ====================
  
  /**
   * Check if user can use date picker feature
   * Allowed for: Admin, Premium, and Elite users
   * Elite users automatically get Premium features since Elite is a higher tier
   * @returns {boolean}
   */
  canUseDatePicker() {
    if (this.isAdmin()) return true;
    // Premium or Elite users have access
    return this.isPremiumUser() || this.isEliteUser();
  }

  /**
   * Check if user can use preview button feature
   * Allowed for: Elite users only
   * @returns {boolean}
   */
  canUsePreviewButton() {
    return this.isEliteUser();
  }

  /**
   * Check if user can use drawing tool feature
   * Allowed for: Admin, Premium, and Elite users
   * Elite users automatically get Premium features since Elite is a higher tier
   * @returns {boolean}
   */
  canUseDrawingTool() {
    if (this.isAdmin()) return true;
    // Premium or Elite users have access
    return this.isPremiumUser() || this.isEliteUser();
  }

  // ==================== DATA POLLING INTERVALS ====================
  
  /**
   * Get chart reload interval based on user role and resources
   * Returns appropriate interval from MP_CHART_RELOAD_TIME constants
   * New logic: 
   * - Free users (no resources) → 70 seconds (delayed)
   * - Premium users (with 1min_data) → 1 minute (live)
   * - Elite users (with tbt_data) → 1.1 seconds (realtime)
   * @returns {number} Polling interval in milliseconds
   */
  getChartReloadInterval() {
    // Import constants (dynamic to avoid circular dependency)
    const { MP_CHART_RELOAD_TIME } = require('./constant');
    
    // Elite users with tbt_data: Real-time polling (1.1 seconds)
    if (this.resources.includes(USER_RESOURCES_LIST.TBT_FEED)) {
      return MP_CHART_RELOAD_TIME.FAST_DATA; // 1.1 seconds
    }
    
    // Premium users with 1min_data: Minute-wise polling (1 minute)
    if (this.resources.includes(USER_RESOURCES_LIST.ONE_MIN_DATA)) {
      return MP_CHART_RELOAD_TIME.MINWISE_DATA; // 1 minute
    }
    
    // Free users (no data resources): Delayed polling (70 seconds)
    return MP_CHART_RELOAD_TIME.SLOW_DATA; // 70 seconds
  }

  /**
   * Get ticker data reload interval based on user role and resources
   * Returns appropriate interval from CHART_RELOAD_TIME constants
   * New logic: Based on data resources (tbt_data, 1min_data, or none)
   * @returns {number} Polling interval in milliseconds
   */
  getTickerReloadInterval() {
    // Import constants (dynamic to avoid circular dependency)
    const { CHART_RELOAD_TIME, MP_CHART_RELOAD_TIME } = require('./constant');
    
    // Elite/Premium users with data resources: Fast polling (2 seconds)
    if (this.resources.includes(USER_RESOURCES_LIST.TBT_FEED) ||
        this.resources.includes(USER_RESOURCES_LIST.ONE_MIN_DATA)) {
      return CHART_RELOAD_TIME.TICKER_DATA_FAST; // 2 seconds
    }
    
    // Free users (no data resources): Delayed polling (70 seconds)
    return MP_CHART_RELOAD_TIME.SLOW_DATA; // 70 seconds
  }

  /**
   * Get KRL (Key Risk Level) polling time based on user role and instrument live status
   * Returns appropriate interval from MP_CHART_RELOAD_TIME constants
   * When instrument is live: Admin users get faster polling, others get standard polling
   * When instrument is not live: Always use standard polling (business logic)
   * @param {boolean} isInstrumentLive - Whether the instrument is currently live (default: false)
   * @returns {number} Polling interval in milliseconds
   */
  getKrlPollingTime(isInstrumentLive = false) {
    // Import constants (dynamic to avoid circular dependency)
    const { MP_CHART_RELOAD_TIME } = require('./constant');
    
    // If instrument is not live, always use standard polling (business logic)
    if (!isInstrumentLive) {
      return MP_CHART_RELOAD_TIME.KRL_POLLING_TIME; // 5 minutes
    }
    
    // When instrument is live: Admin users get fast polling (1 minute)
    if (this.isAdmin()) {
      return MP_CHART_RELOAD_TIME.FAST_KRL_POLLING_TIME; // 1 minute
    }
    
    // When instrument is live: All other users get standard polling (5 minutes)
    return MP_CHART_RELOAD_TIME.KRL_POLLING_TIME; // 5 minutes
  }

  /**
   * Get options table reload interval based on user tier and resources
   * Returns appropriate interval from CHART_RELOAD_TIME constants
   * New logic: Based on data resources (tbt_data, 1min_data, or none)
   * @returns {number} Polling interval in milliseconds
   */
  getOptionsReloadInterval() {
    // Import constants (dynamic to avoid circular dependency)
    const { CHART_RELOAD_TIME, MP_CHART_RELOAD_TIME } = require('./constant');
    
    // Elite users with tbt_data: Fast polling (8 seconds)
    if (this.resources.includes(USER_RESOURCES_LIST.TBT_FEED)) {
      return CHART_RELOAD_TIME.OPTIONS_DATA; // 8 seconds
    }
    
    // Premium users with 1min_data: Moderate polling (8 seconds)
    if (this.resources.includes(USER_RESOURCES_LIST.ONE_MIN_DATA)) {
      return CHART_RELOAD_TIME.OPTIONS_DATA; // 8 seconds
    }
    
    // Free users (no data resources): Delayed polling (70 seconds)
    return MP_CHART_RELOAD_TIME.SLOW_DATA; // 70 seconds
  }

  /**
   * Get greeks table reload interval based on user resources
   * Returns appropriate interval from CHART_RELOAD_TIME constants
   * New logic: Based on data resources (tbt_data, 1min_data, or none)
   * @returns {number} Polling interval in milliseconds
   */
  getGreeksReloadInterval() {
    // Import constants (dynamic to avoid circular dependency)
    const { CHART_RELOAD_TIME, MP_CHART_RELOAD_TIME } = require('./constant');
    
    // Elite/Premium users with data resources: Fast polling (5 seconds)
    if (this.resources.includes(USER_RESOURCES_LIST.TBT_FEED) ||
        this.resources.includes(USER_RESOURCES_LIST.ONE_MIN_DATA)) {
      return CHART_RELOAD_TIME.GREEKS_DATA; // 5 seconds
    }
    
    // Free users (no data resources): Delayed polling (70 seconds)
    return MP_CHART_RELOAD_TIME.SLOW_DATA; // 70 seconds
  }

  /**
   * Get index table reload interval based on user resources
   * Returns appropriate interval from CHART_RELOAD_TIME constants
   * New logic: Based on data resources (tbt_data, 1min_data, or none)
   * @returns {number} Polling interval in milliseconds
   */
  getIndexReloadInterval() {
    // Import constants (dynamic to avoid circular dependency)
    const { CHART_RELOAD_TIME, MP_CHART_RELOAD_TIME } = require('./constant');
    
    // Elite/Premium users with data resources: Fast polling (4 seconds)
    if (this.resources.includes(USER_RESOURCES_LIST.TBT_FEED) || 
        this.resources.includes(USER_RESOURCES_LIST.ONE_MIN_DATA)) {
      return CHART_RELOAD_TIME.INDEX_DATA_FAST; // 4 seconds
    }
    
    // Free users (no data resources): Delayed polling (70 seconds)
    return MP_CHART_RELOAD_TIME.SLOW_DATA; // 70 seconds
  }

  /**
   * Get stock chart reload interval based on user resources
   * Returns appropriate interval from CHART_RELOAD_TIME constants
   * New logic: Based on data resources (tbt_data, 1min_data, or none)
   * @returns {number} Polling interval in milliseconds
   */
  getStockChartReloadInterval() {
    // Import constants (dynamic to avoid circular dependency)
    const { CHART_RELOAD_TIME, MP_CHART_RELOAD_TIME } = require('./constant');
    
    // Elite users with tbt_data: Real-time polling (1.1 seconds)
    if (this.resources.includes(USER_RESOURCES_LIST.TBT_FEED)) {
      return CHART_RELOAD_TIME.FAST_DATA; // 1.1 seconds
    }
    
    // Premium users with 1min_data: Minute-wise polling (1 minute)
    if (this.resources.includes(USER_RESOURCES_LIST.ONE_MIN_DATA)) {
      return MP_CHART_RELOAD_TIME.MINWISE_DATA; // 1 minute
    }
    
    // Free users (no data resources): Delayed polling (70 seconds)
    return MP_CHART_RELOAD_TIME.SLOW_DATA; // 70 seconds
  }

  // ==================== WATCHLIST ACCESS ====================
  
  /**
   * Check if user can access watchlist feature
   * Uses WATCHLIST_ALLOWED_ROLES constant for maintainability
   * Currently only allows admin, but can be extended in the future
   * @returns {boolean}
   */
  canAccessWatchlist() {
    // Import constants (dynamic to avoid circular dependency)
    const { WATCHLIST_ALLOWED_ROLES } = require('./constant');
    
    // Check if user has any of the allowed roles for watchlist
    return this.roles.some(role => WATCHLIST_ALLOWED_ROLES.includes(role));
  }

  // ==================== DYNAMIC PERMISSION CHECKS ====================
  
  /**
   * Check if user has access based on dynamic role and resource arrays
   * Used for drawer menu items and other dynamic permission checks
   * @param {Array<string>|undefined} requiredRoles - Array of allowed roles (undefined = no restriction)
   * @param {Array<string>|undefined} requiredResources - Array of required resources (undefined = no restriction)
   * @returns {boolean}
   */
  hasAccess(requiredRoles, requiredResources) {
    // No role requirement = accessible by all (but we still check resources if specified)
    if (requiredRoles === undefined || requiredRoles === null || requiredRoles.length === 0) {
      // Only check resources if specified
      if (requiredResources === undefined || requiredResources === null || requiredResources.length === 0) {
        return true; // No restrictions = public access
      }
      // Check resources only
      return this.hasAllResources(requiredResources);
    }
    
    // Check if user has at least one of the required roles
    const hasRole = this.roles.some(role => requiredRoles.includes(role));
    if (!hasRole) return false;
    
    // Check resources if specified
    if (requiredResources !== undefined && requiredResources !== null && requiredResources.length > 0) {
      return this.hasAllResources(requiredResources);
    }
    
    return true; // Has role and no resource requirement
  }
}

/**
 * Factory function to create permission service
 * @param {Object} auth - Auth object
 * @returns {PermissionService}
 */
export const createPermissionService = (auth) => {
  return new PermissionService(auth);
};

// Export FEATURES for easy imports
export { FEATURES };

